<template>
  <div class="attendance-page">
    <Loader :active="isLoading" message="অবস্থান যাচাই করা হচ্ছে..." />
    <Navbar />

    <b-modal 
      v-model="showModal" 
      :title="modalTitle"
      :header-bg-variant="modalVariant"
      header-text-variant="light"
      ok-only
      centered
      ok-title="ঠিক আছে" 
      @ok="handleModalOk"
      @hidden="handleModalClose"
    >
      <p class="my-2 text-center" style="font-size: 1.1rem;">{{ modalMessage }}</p>
    </b-modal>
    
    <div class="hero-header">
      <div class="container pt-4 pb-5 text-center text-white">
        <h5 class="opacity-75 mb-1 font-weight-normal">শুভ দিন, আজ</h5>
        <h6 class="mb-3 opacity-90">{{ currentDate }}</h6>
        
        <div class="digital-clock">
          {{ currentTime }}
        </div>
      </div>
    </div>

    <div class="container main-content-container">
      <div class="row justify-content-center">
        <div class="col-md-6 col-lg-5">
          
          <div class="card status-card border-0 shadow-lg mb-4 animate-up">
            <div class="card-body p-4 text-center">
              
              <div class="gps-badge-container mb-4">
                 <div 
                  class="gps-pill" 
                  :class="locationReady ? 'gps-active' : 'gps-searching'"
                  @click="!locationReady ? requestLocation() : null"
                >
                  <i :class="locationReady ? 'fas fa-map-marker-alt' : 'fas fa-search-location'"></i>
                  
                  <span v-if="locationReady" class="mx-2">GPS Connected</span>
                  <span v-else class="mx-2">Finding Location...</span>
                  <span v-if="locationReady" class="coord-text">({{ locationCoords.lat.toFixed(4) }}, {{ locationCoords.lng.toFixed(4) }})</span>
                </div>
              </div>

              <div class="status-ring mb-3 mx-auto" :class="userStatus.is_present ? 'ring-success' : 'ring-inactive'">
                <i v-if="userStatus.is_present" class="fas fa-check fa-3x"></i>
                <i v-else class="fas fa-briefcase fa-3x"></i>
              </div>

              <h4 class="font-weight-bold mb-1" :class="userStatus.is_present ? 'text-success' : 'text-muted'">
                {{ userStatus.is_present ? 'পাঞ্চ ইন করা হয়েছে' : 'আপনি এখন বাইরে' }}
              </h4>
              
              <p v-if="userStatus.is_present" class="text-muted mb-0">
                প্রবেশের সময়: <span class="text-dark font-weight-bold">{{ formatTime(userStatus.entry_time) }}</span>
              </p>
              <p v-else class="text-muted mb-0" style="font-size: 0.9rem;">
                কাজ শুরু করতে নিচে পাঞ্চ ইন করুন
              </p>

            </div>
          </div>

          <div class="action-area animate-up-delay">
            
            <button 
              v-if="!userStatus.is_present"
              @click="processAttendance('IN')" 
              class="btn btn-punch-in btn-block shadow"
              :disabled="!locationReady"
            >
               <div class="d-flex align-items-center justify-content-center">
                 <div class="icon-box mr-3"><i class="fas fa-fingerprint"></i></div>
                 <div class="text-left">
                   <span class="d-block action-title">পাঞ্চ ইন করুন</span>
                   <span class="d-block action-subtitle">দিন শুরু করতে ট্যাপ করুন</span>
                 </div>
               </div>
            </button>

            <button 
              v-else
              @click="processAttendance('OUT')" 
              class="btn btn-punch-out btn-block shadow"
              :disabled="userStatus.exit_time || !locationReady"
            >
              <div class="d-flex align-items-center justify-content-center">
                 <div class="icon-box mr-3"><i class="fas fa-power-off"></i></div>
                 <div class="text-left">
                   <span class="d-block action-title">পাঞ্চ আউট করুন</span>
                   <span class="d-block action-subtitle">কাজ শেষ করতে ট্যাপ করুন</span>
                 </div>
               </div>
            </button>

            <div class="text-center mt-4">
              <small class="text-muted opacity-75">
                <i class="fas fa-shield-alt mr-1"></i>
                সিকিউর লোকেশন ভেরিফিকেশন চালু আছে
              </small>
            </div>

          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Navbar from './components/Navbar.vue'
  import Loader from './components/Loader.vue'
  import { getAPI } from './axios-api.js'
  import { BModal } from 'bootstrap-vue'
  
  export default {
    name: 'EmployeeAttendance',
    components: { 
      Navbar, 
      Loader,
      'b-modal': BModal
    },
    data() {
      return {
        isLoading: false,
        currentTime: '--:--:--',
        currentDate: '',
        locationReady: false,
        locationCoords: null,
        userStatus: {
          is_present: this.$store.state.hasPunchedIn === true,
          entry_time: this.$store.state.punchTime, 
          exit_time: null,
        },
        showModal: false,
        modalTitle: '',
        modalMessage: '',
        modalVariant: 'info',
        shouldReloadOnClose: false
      }
    },
    mounted() {
      this.startClock();
      this.requestLocation();
      this.startLocationWatcher();
      this.verifyServerStatus();
    },
    beforeDestroy() {
      if (this.geoId !== null) {
        navigator.geolocation.clearWatch(this.geoId);
      }
    },
    methods: {
      startClock() {
        this.currentDate = new Date().toLocaleDateString('bn-BD', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        setInterval(() => {
          this.currentTime = new Date().toLocaleTimeString('bn-BD');
        }, 1000);
      },

      formatTime(timeString) {
        if (!timeString) return '--:--';
        const [hours, minutes] = timeString.split(':');
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);
        return date.toLocaleTimeString('bn-BD', { hour: '2-digit', minute: '2-digit' });
      },

      verifyServerStatus() {
         getAPI.get('/attendance/status/', {
              headers: { Authorization: `Bearer ${this.$store.state.accessToken}` }
         }).then(res => {
             this.userStatus.is_present = res.data.has_punched_in;
             if(res.data.punch_in_time) {
                 this.userStatus.entry_time = res.data.punch_in_time;
                 this.$store.commit('setAttendanceData', {
                    status: true,
                    time: res.data.punch_in_time
                 });
             }
         })
      },

      showNotification(title, message, variant = 'primary', shouldReload = false) {
          this.modalTitle = title;
          this.modalMessage = message;
          this.modalVariant = variant;
          this.shouldReloadOnClose = shouldReload;
          this.showModal = true;
      },
      handleModalOk() {
          if (this.shouldReloadOnClose) this.$router.go();
          this.showModal = false;
      },
      handleModalClose() {
          if (this.shouldReloadOnClose) this.$router.go();
      },
  
      requestLocation() {
        // 1. Check if Geolocation is supported
        if (!("geolocation" in navigator)) {
          this.showNotification("ত্রুটি", "আপনার ডিভাইসে জিপিএস সুবিধা নেই।", "danger");
          return;
        }

        this.isLoading = true; // Show loader while trying

        const options = {
          enableHighAccuracy: true,
          timeout: 10000, // Wait 10 seconds max
          maximumAge: 0   // Do not use cached location
        };

        navigator.geolocation.getCurrentPosition(
          // Success Callback
          (position) => {
            this.locationCoords = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            this.locationReady = true;
            this.isLoading = false;
            // Optional: Success message for debugging
            // console.log("GPS Location Found:", this.locationCoords); 
          },
          // Error Callback
          (error) => {
            this.isLoading = false;
            this.locationReady = false;
            console.error("GPS Error:", error);

            let errorMsg = "আপনার ডিভাইসের জিপিএস বন্ধ আছে। দয়া করে জিপিএস চালু করুন।";
            
            this.showNotification("জিপিএস ত্রুটি", errorMsg, "danger");
          },
          options
        );
      },
  
      async processAttendance(type) {
        this.isLoading = true;
  
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.locationCoords = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
  
            const payload = {
              type: type, 
              lat: this.locationCoords.lat,
              lng: this.locationCoords.lng
            };
  
            getAPI.post('/attendance/submit/', payload, {
              headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
            })
            .then(() => {
              const timeNow = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
              
              if (type === 'IN') {
                this.userStatus.is_present = true;
                this.userStatus.entry_time = timeNow;
                this.$store.commit('setAttendanceData', { status: true, time: timeNow });
                this.showNotification("সফল", "পাঞ্চ ইন সফল হয়েছে!", "success");
                setTimeout(() => {
                  this.$router.push({name:'posts'})
                }, 2000);
              } else {
                this.userStatus.is_present = false;
                this.userStatus.exit_time = timeNow;
                this.$store.commit('setAttendanceData', { status: false, time: null });
                this.showNotification("ধন্যবাদ", "পাঞ্চ আউট সফল হয়েছে! লগ আউট করা হচ্ছে...", "warning");
                setTimeout(() => {
                  this.$store.dispatch('logoutUser');
                }, 2000);
              }
              
            })
            .catch(err => {
              console.error(err);
              this.showNotification("নেটওয়ার্ক ত্রুটি", "উপস্থিতি সংরক্ষণ করা সম্ভব হয়নি। আপনার ইন্টারনেট সংযোগ পরীক্ষা করুন।", "danger");
            })
            .finally(() => {
              this.isLoading = false;
            });
          },
          (error) => {
            console.error(error);
            this.isLoading = false;
            this.showNotification("জিপিএস ত্রুটি", "জিপিএস পাওয়া যাচ্ছে না। দয়া করে লোকেশন পারমিশন দিন এবং আবার চেষ্টা করুন।", "danger");
          },
          { enableHighAccuracy: true }
        );
      }
    }
  }
</script>

<style scoped>
.attendance-page { 
  background-color: #f4f6f9; 
  min-height: 100vh; 
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* --- HERO HEADER --- */
.hero-header {
  background: linear-gradient(135deg, #90288b 0%, #6a1b9a 100%);
  border-bottom-left-radius: 30px;
  border-bottom-right-radius: 30px;
  padding-bottom: 80px !important;
  box-shadow: 0 10px 20px rgba(144, 40, 139, 0.2);
}

.digital-clock {
  font-size: 3.5rem;
  font-weight: 300;
  letter-spacing: 2px;
  line-height: 1;
  text-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.main-content-container {
  margin-top: -60px;
}

/* --- STATUS CARD --- */
.status-card {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0,0,0,0.08) !important;
}

.status-ring {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  transition: all 0.3s ease;
}

.ring-success {
  background-color: #e8f5e9;
  color: #28a745;
  border: 4px solid #c3e6cb;
  animation: gentlePulse 2s infinite;
}

.ring-inactive {
  background-color: #f8f9fa;
  color: #adb5bd;
  border: 4px solid #e9ecef;
}

/* --- GPS PILL --- */
.gps-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 15px;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.gps-active {
  background-color: #e8f5e9;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.gps-searching {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
  animation: blink 1.5s infinite;
}

.coord-text {
  font-weight: normal;
  opacity: 0.7;
  font-size: 0.75rem;
  margin-left: 5px;
}

/* --- BUTTONS --- */
.btn-punch-in {
  background: linear-gradient(45deg, #11998e, #38ef7d);
  color: white;
  border: none;
  border-radius: 15px;
  padding: 15px 20px;
  transition: transform 0.2s;
}

.btn-punch-out {
  background: white;
  color: #dc3545;
  border: 2px solid #dc3545;
  border-radius: 15px;
  padding: 15px 20px;
  transition: all 0.2s;
}
.btn-punch-out:hover {
  background: #fff5f5;
}

.icon-box {
  background: rgba(255,255,255,0.2);
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}
.btn-punch-out .icon-box {
  background: rgba(220, 53, 69, 0.1);
}

.action-title {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.2;
}

.action-subtitle {
  font-size: 0.8rem;
  opacity: 0.9;
  font-weight: 400;
}

.btn:active {
  transform: scale(0.98);
}

/* --- ANIMATIONS --- */
.animate-up {
  animation: slideUp 0.6s ease-out;
}
.animate-up-delay {
  animation: slideUp 0.6s ease-out 0.2s backwards;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes gentlePulse {
  0% { box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(40, 167, 69, 0); }
  100% { box-shadow: 0 0 0 0 rgba(40, 167, 69, 0); }
}

@keyframes blink {
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
}

@media (max-width: 576px) {
  .digital-clock {
    font-size: 2.8rem;
  }
  .hero-header {
    padding-bottom: 60px !important;
  }
}
</style>